import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, arrayRemove, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getMaxImagesByTier } from '../../utils/imageUtils';
import { approveImageWithChecks, rejectImageWithChecks } from '../../utils/adminImageUtils';

const ImageApprovalPanel = () => {
  const [pendingImages, setPendingImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [searchTerm, setSearchTerm] = useState('');

  // ดึงข้อมูลรูปรออนุมัติ
  const fetchPendingImages = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching pending images...');
      
      const girlsQuery = query(collection(db, 'girls'));
      const girlsSnapshot = await getDocs(girlsQuery);
      
      const allPendingImages = [];
      
      for (const girlDoc of girlsSnapshot.docs) {
        const girlData = girlDoc.data();
        const girlId = girlDoc.id;
        
        console.log(`👤 Processing girl ${girlId}:`, {
          nickname: girlData.nickname,
          pendingImagesCount: girlData.pendingImages?.length || 0,
          approvedImagesCount: girlData.images?.length || 0
        });
        
        // ดึงข้อมูล tier จาก users collection
        const userRef = doc(db, 'users', girlId);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};
        
        // Debug logs สำหรับ tier data
        console.log(`⭐ Fetching tier for girl ${girlId}:`, userData.tier || 'free');
        
        if (girlData.pendingImages && girlData.pendingImages.length > 0) {
          // ตรวจสอบรูปซ้ำใน pending images
          const existingImages = Array.isArray(girlData.images) ? girlData.images : [];
          const cleanedPendingImages = [];
          
          girlData.pendingImages.forEach((image, index) => {
            // ตรวจสอบว่ามีรูปซ้ำใน approved images หรือไม่
            const isDuplicate = existingImages.some(existingImg => 
              existingImg.fileHash === image.fileHash || 
              existingImg.url === image.url ||
              (existingImg.originalName === image.originalName && 
               existingImg.originalSize === image.originalSize)
            );
            
            if (isDuplicate) {
              console.log(`❌ Found duplicate pending image:`, {
                girlId,
                imageId: image.id,
                fileName: image.fileName,
                originalName: image.originalName,
                url: image.url
              });
              
              // ลบรูปซ้ำออกจากฐานข้อมูลทันที
              updateDoc(doc(db, 'girls', girlId), {
                pendingImages: arrayRemove(image),
                lastUpdated: new Date().toISOString()
              }).then(() => {
                console.log(`✅ Removed duplicate pending image from database: ${image.fileName}`);
              }).catch(error => {
                console.error(`❌ Error removing duplicate image:`, error);
              });
            } else {
              cleanedPendingImages.push({
                ...image,
                id: `${girlId}_${index}`,
                girlId,
                girlName: girlData.nickname || 'ไม่ระบุชื่อ',
                girlData: {
                  nickname: girlData.nickname,
                  type: girlData.type,
                  location: girlData.location,
                  tier: userData.tier || 'free'
                }
              });
            }
          });
          
          console.log(`📊 Girl ${girlId} pending images:`, {
            original: girlData.pendingImages.length,
            cleaned: cleanedPendingImages.length,
            duplicates: girlData.pendingImages.length - cleanedPendingImages.length
          });
          
          allPendingImages.push(...cleanedPendingImages);
        }
      }
      
      // เรียงตามวันที่อัพโหลด (ใหม่สุดก่อน)
      allPendingImages.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      
      console.log('📊 Final pending images:', {
        total: allPendingImages.length,
        byGirl: allPendingImages.reduce((acc, img) => {
          acc[img.girlId] = (acc[img.girlId] || 0) + 1;
          return acc;
        }, {})
      });
      
      setPendingImages(allPendingImages);
    } catch (error) {
      console.error('💥 Error fetching pending images:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingImages();
  }, []);

  // กรองรูปภาพตาม filter
  const filteredImages = pendingImages.filter(image => {
    if (filter === 'all') return true;
    return image.status === filter;
  });

  // ค้นหารูปภาพ
  const searchedImages = filteredImages.filter(image => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      image.girlName.toLowerCase().includes(searchLower) ||
      image.originalName?.toLowerCase().includes(searchLower) ||
      image.girlData.location?.toLowerCase().includes(searchLower)
    );
  });

  // อนุมัติรูปภาพ
  const approveImage = async (imageId, girlId) => {
    try {
      console.log('🚀 Admin Approval Process Started:');
      console.log('- Image ID:', imageId);
      console.log('- Girl ID:', girlId);
      console.log('- Pending Images Count:', pendingImages.length);
      
      // หารูปที่ต้องการอนุมัติ
      const imageToApprove = pendingImages.find(img => img.id === imageId);
      if (!imageToApprove) {
        console.log('❌ Image not found in pending images');
        alert('ไม่พบรูปภาพที่ต้องการอนุมัติ');
        return;
      }
      
      console.log('📷 Image to Approve:', {
        id: imageToApprove.id,
        fileName: imageToApprove.fileName,
        originalName: imageToApprove.originalName,
        url: imageToApprove.url,
        fileHash: imageToApprove.fileHash,
        originalSize: imageToApprove.originalSize
      });
      
      const result = await approveImageWithChecks(imageId, girlId, pendingImages);
      
      if (result.success) {
        console.log('✅ Approval successful, updating state...');
        // อัพเดท state
        setPendingImages(prev => {
          const filtered = prev.filter(img => img.id !== imageId);
          console.log('- Pending images after removal:', filtered.length);
          return filtered;
        });
        alert(result.message);
      } else {
        console.log('❌ Approval failed:', result.message);
        alert(result.message);
      }
    } catch (error) {
      console.error('💥 Error approving image:', error);
      alert('เกิดข้อผิดพลาดในการอนุมัติ');
    }
  };

  // ปฏิเสธรูปภาพ
  const rejectImage = async (imageId, girlId, reason = '') => {
    try {
      const result = await rejectImageWithChecks(imageId, girlId, pendingImages, reason);
      
      if (result.success) {
        // อัพเดท state
        setPendingImages(prev => prev.filter(img => img.id !== imageId));
        alert(result.message);
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error rejecting image:', error);
      alert('เกิดข้อผิดพลาดในการปฏิเสธ');
    }
  };

  // ดูรูปภาพแบบเต็ม
  const viewImage = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'รออนุมัติ';
      case 'approved': return 'อนุมัติแล้ว';
      case 'rejected': return 'ปฏิเสธแล้ว';
      default: return 'ไม่ทราบสถานะ';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">ระบบอนุมัติรูปภาพ</h1>
        <button
          onClick={fetchPendingImages}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          รีเฟรช
        </button>
      </div>

      {/* ตัวกรองและค้นหา */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="ค้นหาตามชื่อน้อง, ชื่อไฟล์, หรือสถานที่..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">ทั้งหมด</option>
            <option value="pending">รออนุมัติ</option>
            <option value="approved">อนุมัติแล้ว</option>
            <option value="rejected">ปฏิเสธแล้ว</option>
          </select>
        </div>
      </div>

      {/* สถิติ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">
            {pendingImages.filter(img => img.status === 'pending').length}
          </div>
          <div className="text-sm text-blue-700">รออนุมัติ</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {pendingImages.filter(img => img.status === 'approved').length}
          </div>
          <div className="text-sm text-green-700">อนุมัติแล้ว</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-red-600">
            {pendingImages.filter(img => img.status === 'rejected').length}
          </div>
          <div className="text-sm text-red-700">ปฏิเสธแล้ว</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-gray-600">
            {pendingImages.length}
          </div>
          <div className="text-sm text-gray-700">ทั้งหมด</div>
        </div>
      </div>

      {/* รายการรูปภาพ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {searchedImages.map((image) => (
          <div key={image.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* รูปภาพ */}
            <div 
              className="aspect-square cursor-pointer"
              onClick={() => viewImage(image)}
            >
              <img
                src={image.url}
                alt={image.originalName || 'Image'}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDMwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRkZCNkMxIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiMzMzMiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltYWdlIEVycm9yPC90ZXh0Pgo8L3N2Zz4K';
                }}
              />
            </div>

            {/* ข้อมูลรูปภาพ */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">{image.girlName}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(image.status)}`}>
                  {getStatusText(image.status)}
                </span>
              </div>

              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>ไฟล์:</strong> {image.originalName || 'ไม่ระบุ'}</p>
                <p><strong>สถานที่:</strong> {image.girlData.location || 'ไม่ระบุ'}</p>
                <p><strong>ประเภท:</strong> {image.girlData.type || 'ไม่ระบุ'}</p>
                <p><strong>อัพโหลด:</strong> {new Date(image.uploadedAt).toLocaleString('th-TH')}</p>
                {(() => {
                  const maxImages = getMaxImagesByTier(image.girlData.tier || 'free', 'girl');
                  return (
                    <p><strong>Tier:</strong> {image.girlData.tier || 'free'} ({maxImages === -1 ? 'ไม่จำกัด' : `${maxImages} รูป`})</p>
                  );
                })()}
              </div>

              {/* ปุ่มจัดการ */}
              {image.status === 'pending' && (
                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={() => approveImage(image.id, image.girlId)}
                    className="flex-1 px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm"
                  >
                    ✅ อนุมัติ
                  </button>
                  <button
                    onClick={() => {
                      const reason = prompt('เหตุผลในการปฏิเสธ (ไม่บังคับ):');
                      rejectImage(image.id, image.girlId, reason);
                    }}
                    className="flex-1 px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                  >
                    ❌ ปฏิเสธ
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal ดูรูปภาพ */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">ดูรูปภาพ</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <img
                src={selectedImage.url}
                alt="Preview"
                className="w-full max-h-96 object-contain rounded-lg"
              />
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>ชื่อน้อง:</strong> {selectedImage.girlName}
                </div>
                <div>
                  <strong>สถานะ:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(selectedImage.status)}`}>
                    {getStatusText(selectedImage.status)}
                  </span>
                </div>
                <div>
                  <strong>ชื่อไฟล์:</strong> {selectedImage.originalName || 'ไม่ระบุ'}
                </div>
                <div>
                  <strong>สถานที่:</strong> {selectedImage.girlData.location || 'ไม่ระบุ'}
                </div>
                <div>
                  <strong>ประเภท:</strong> {selectedImage.girlData.type || 'ไม่ระบุ'}
                </div>
                <div>
                  <strong>อัพโหลดเมื่อ:</strong> {new Date(selectedImage.uploadedAt).toLocaleString('th-TH')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ข้อความเมื่อไม่มีรูป */}
      {searchedImages.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📷</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">ไม่มีรูปภาพ</h3>
          <p className="text-gray-600">ไม่มีรูปภาพที่ตรงกับเงื่อนไขการค้นหา</p>
        </div>
      )}
    </div>
  );
};

export default ImageApprovalPanel;
